import { useState } from "react";
import type { ParsedGame } from "@onchainreps/chess";
import { CheckIcon, CopyIcon } from "./Icons";
import type { GameReport, MoveReview } from "../lib/analysis";

interface ReportPanelProps {
  game: ParsedGame;
  report: GameReport;
  reviews: MoveReview[];
  onSelect: (ply: number) => void;
}

const CATEGORY_ORDER: Array<{ key: keyof GameReport["counts"]; label: string; color: string }> = [
  { key: "brilliant", label: "Brilliant", color: "var(--violet)" },
  { key: "great", label: "Great", color: "var(--cobalt)" },
  { key: "best", label: "Best", color: "var(--moss)" },
  { key: "inaccuracy", label: "Inaccuracy", color: "var(--amber)" },
  { key: "miss", label: "Miss", color: "#d38b12" },
  { key: "blunder", label: "Blunder", color: "var(--crimson)" },
];

export function ReportPanel({ game, report, reviews, onSelect }: ReportPanelProps) {
  const totalMoves = game.moves.length || 1;
  const worstMove = report.worst ? game.moves[report.worst.ply] : null;
  const worstWhite = worstMove?.color === "white";
  const [copied, setCopied] = useState(false);

  const keyMoments = reviews
    .filter((review) => review.loss >= 5 && review.category !== "best")
    .sort((a, b) => b.loss - a.loss)
    .slice(0, 5);

  const copySummary = () => {
    const lines = [
      `${game.headers.White ?? "White"} vs ${game.headers.Black ?? "Black"}`,
      `White accuracy ${report.accuracyWhite.toFixed(1)}%, Black accuracy ${report.accuracyBlack.toFixed(1)}%`,
      `Brilliant ${report.counts.brilliant}, Great ${report.counts.great}, Best ${report.counts.best}, Inaccuracy ${report.counts.inaccuracy}, Miss ${report.counts.miss}, Blunder ${report.counts.blunder}`,
    ];
    if (report.worst) {
      const move = game.moves[report.worst.ply];
      if (move) {
        lines.push(
          `Costliest mistake: ${move.color === "white" ? "White" : "Black"} ${move.san} on move ${move.moveNumber} (-${report.worst.loss.toFixed(1)}%)`,
        );
      }
    }
    lines.push("Analysed with OnchainReps");
    void navigator.clipboard
      ?.writeText(lines.join("\n"))
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      })
      .catch(() => undefined);
  };

  return (
    <div>
      <div className="metrics">
        <div className="metric">
          <div className="metric__label">White accuracy</div>
          <div className="metric__value metric__value--white">{report.accuracyWhite.toFixed(1)}%</div>
          <div className="metric__note">{game.headers.White ?? "White"}</div>
        </div>
        <div className="metric">
          <div className="metric__label">Black accuracy</div>
          <div className="metric__value metric__value--black">{report.accuracyBlack.toFixed(1)}%</div>
          <div className="metric__note">{game.headers.Black ?? "Black"}</div>
        </div>
      </div>

      <div className="breakdown">
        {CATEGORY_ORDER.map((category) => {
          const count = report.counts[category.key];
          const share = (count / totalMoves) * 100;
          return (
            <div className="breakdown__row" key={category.key}>
              <span>{category.label}</span>
              <span className="breakdown__bar">
                <span
                  className="breakdown__fill"
                  style={{ width: `${share}%`, background: category.color }}
                />
              </span>
              <span className="breakdown__count">{count}</span>
            </div>
          );
        })}
      </div>

      {report.worst && worstMove ? (
        <div className="callout">
          <h3 className="callout__title">Costliest mistake</h3>
          <p className="callout__body">
            {worstWhite ? "White" : "Black"} played {worstMove.san} on move {worstMove.moveNumber}, giving up{" "}
            {report.worst.loss.toFixed(1)}% evaluation. {report.worst.sacrifice ? "It was a sacrifice that did not work." : ""}
          </p>
          <button className="btn btn--inline" onClick={() => onSelect(report.worst!.ply + 1)}>
            Jump to the position
          </button>
        </div>
      ) : null}

      {keyMoments.length ? (
        <div className="moments">
          <h3 className="moments__title">Key moments</h3>
          <ul className="moments__list">
            {keyMoments.map((moment) => {
              const move = game.moves[moment.ply];
              if (!move) return null;
              return (
                <li key={moment.ply}>
                  <button className="moment" onClick={() => onSelect(moment.ply + 1)}>
                    <span className="moment__move">
                      <span className="moment__number">{move.moveNumber}.</span>
                      {move.san}
                    </span>
                    <span className="moment__meta">
                      {move.color === "white" ? "White" : "Black"} · {moment.category} · -
                      {moment.loss.toFixed(1)}%
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      <button className="btn btn--ghost btn--wide" onClick={copySummary}>
        {copied ? <CheckIcon /> : <CopyIcon />}
        {copied ? "Summary copied" : "Copy the report"}
      </button>
    </div>
  );
}
